import * as React from 'react';
import {useNavigate} from 'react-router-dom';
import {Stack} from '@discordapp/design/web';
import {useStateFromStores} from '@discordapp/flux';

import * as ApplicationActions from '@developers/actions/ApplicationActions';
import AvatarSelectOption, {SelectOptionWithAvatar} from '@developers/components/common/AvatarSelectOption';
import Modal, {
  ModalHeader,
  ModalContent,
  ModalFooter,
  ModalCancel,
  ModalAlert,
  ModalAlertTypes,
} from '@developers/components/common/Modal';
import TeamStore from '@developers/stores/TeamStore';
import UserStore from '@developers/stores/UserStore';
import Button from '@developers/uikit/Button';
import Checkbox from '@developers/uikit/Checkbox';
import Select from '@developers/uikit/Select';
import TextInput from '@developers/uikit/TextInput';
import Form from '@developers/uikit/form/Form';
import FormLabel from '@developers/uikit/form/FormLabel';
import {getUserAvatarURL} from '@developers/utils/AvatarUtils';
import {prependBasename} from '@developers/utils/RouterUtils';
import {getTeamSelectOptions} from '@developers/utils/TeamUtils';

import {Routes, Links} from '@developers/Constants';
import {Messages} from '@developers/i18n';
import type {ErrorWithStatusCode} from '@developers/flow/Client';
import type {Application, AppOwnerId} from '@developers/flow/Server';
import styles from './CreateApplicationModal.module.css';

interface Props {
  onRequestClose: () => void;
}

interface FormData {
  name?: string;
}

export default function CreateApplicationModal({onRequestClose}: Props) {
  const navigate = useNavigate();
  const user = useStateFromStores([UserStore], () => UserStore.user);
  const teams = useStateFromStores([TeamStore], () => TeamStore.teamsMap);

  const [hasAcceptedTermsOfService, setHasAcceptedTermsOfService] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [selectedOwnerId, setSelectedOwnerId] = React.useState<AppOwnerId | null | undefined>(null);
  const [error, setError] = React.useState<React.ReactNode | null | undefined>(null);

  const handleCreateNewApp = (formData: FormData): void => {
    if (user == null) return;

    if (formData == null || formData.name == null || formData.name === '') {
      setError(Messages.MY_APPS_ERROR_MUST_PROVIDE_NAME);
      return;
    }

    if (!hasAcceptedTermsOfService) {
      setError(
        Messages.MY_APPS_ERROR_MUST_ACCEPT_TERMS_OF_SERVICE.format({
          link: prependBasename(Routes.DOCS_POLICIES_AND_AGREEMENTS_DEVELOPER_TERMS_OF_SERVICE),
        }),
      );
      return;
    }

    const appData: {
      name: string;
      team_id?: string | null;
    } = {name: formData.name};

    if (selectedOwnerId !== user.id) {
      appData.team_id = selectedOwnerId;
    }

    setIsSubmitting(true);
    ApplicationActions.createApplication(appData)
      .then((application: Application): void => {
        setIsSubmitting(false);
        setError(null);
        navigate(Routes.APPLICATION_INFORMATION(application.id));
      })
      .catch((error: ErrorWithStatusCode): void => {
        setIsSubmitting(false);
        setError(error.message);
      });
  };

  const handleTeamSelect = <T extends string>(option: SelectOptionWithAvatar<T>): void => {
    setSelectedOwnerId(option.value);
  };

  const renderFormStatus = (): React.ReactNode => {
    if (error == null) return null;
    return <ModalAlert type={ModalAlertTypes.ERROR}>{error}</ModalAlert>;
  };

  const renderTeamOption = <T,>(option: SelectOptionWithAvatar<T>): React.ReactNode => {
    return <AvatarSelectOption option={option} />;
  };

  const renderTeamValue = <T,>(option: SelectOptionWithAvatar<T>): React.ReactNode => {
    return <AvatarSelectOption option={option} isValue />;
  };

  const getTeamSelectOptionsHandler = (): Array<SelectOptionWithAvatar<string>> => {
    if (user == null) {
      return [];
    }
    return getTeamSelectOptions(teams, [
      {label: Messages.MY_APPS_PERSONAL, value: user.id, avatar: getUserAvatarURL(user)},
    ]);
  };

  const handleHasAcceptedTermsOfServiceChange = (hasAccepted: boolean) => {
    setHasAcceptedTermsOfService(hasAccepted);
  };

  const handleRequestClose = (): void => {
    if (!isSubmitting) {
      onRequestClose();
      setHasAcceptedTermsOfService(false);
      setIsSubmitting(false);
      setSelectedOwnerId(null);
      setError(null);
    }
  };

  const renderTeamSelect = (): React.ReactNode => {
    if (teams.size === 0 || user == null) return null;
    return (
      <div>
        <FormLabel htmlFor="appteam">{Messages.MY_APPS_TEAM}</FormLabel>
        <Select
          id="appteam"
          clearable={false}
          searchable={false}
          value={selectedOwnerId == null ? user.id : selectedOwnerId}
          valueRenderer={renderTeamValue}
          optionRenderer={renderTeamOption}
          options={getTeamSelectOptionsHandler()}
          onChange={handleTeamSelect}
        />
      </div>
    );
  };

  return (
    <Modal onRequestClose={handleRequestClose} className={styles.overflowOverride}>
      <Form onSubmit={handleCreateNewApp}>
        <ModalContent>
          <ModalHeader>{Messages.MY_APPS_CREATE_APPLICATION_MODAL_HEADING}</ModalHeader>
          {renderFormStatus()}
          <Stack gap={20}>
            <div>
              <FormLabel isRequired htmlFor="appname">
                {Messages.FORMS_NAME}
              </FormLabel>
              <TextInput name="name" id="appname" defaultValue="" />
            </div>
            {renderTeamSelect()}
            <div className={styles.legalLinksSection}>
              <Checkbox
                value={hasAcceptedTermsOfService}
                onChange={handleHasAcceptedTermsOfServiceChange}
                type={Checkbox.Types.INVERTED}>
                {Messages.INFORMATION_SUBHEADING_LINK.format({
                  developerPolicyLink: Links.DEVELOPER_POLICY,
                  termsOfServiceLink: Links.TERMS_OF_SERVICE,
                })}
              </Checkbox>
            </div>
          </Stack>
        </ModalContent>
        <ModalFooter>
          <ModalCancel onClick={handleRequestClose}>{Messages.ACTIONS_CANCEL}</ModalCancel>
          <Button className={styles.createButton} type="submit" submitting={isSubmitting}>
            {Messages.ACTIONS_CREATE}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}
